/**
 * Review view component
 * Weekly charts, streaks, habit adherence, export
 */

'use client';

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/Skeleton';
import { BarChart3, Download } from 'lucide-react';
import { getLocalStreak } from '@/lib/storage/localRitual';
import { getLocalHabits } from '@/lib/storage/localHabits';
import { getRitualHistory } from '@/lib/services/ritual';

export function ReviewView() {
  const [streak, setStreak] = useState(0);
  const [ritualCompletion, setRitualCompletion] = useState({
    percentage: 0,
    completed: 0,
    total: 7,
  });
  const [habitAdherence, setHabitAdherence] = useState({ percentage: 0, average: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    try {
      setLoading(true);

      // Load streak
      const currentStreak = await getLocalStreak();
      setStreak(currentStreak);

      // Load ritual completion for this week
      try {
        const history = await getRitualHistory(30);
        const thisWeek = history.filter((r) => {
          if (!r.completed_at) return false;
          const completed = new Date(r.completed_at);
          const weekStart = new Date();
          weekStart.setDate(weekStart.getDate() - weekStart.getDay());
          weekStart.setHours(0, 0, 0, 0);
          return completed >= weekStart;
        });
        setRitualCompletion({
          percentage: Math.round((thisWeek.length / 7) * 100),
          completed: thisWeek.length,
          total: 7,
        });
      } catch {
        // Fallback if API fails
        setRitualCompletion({ percentage: 0, completed: 0, total: 7 });
      }

      // Load habit adherence
      const habits = await getLocalHabits();
      if (habits.length > 0) {
        const totalStreak = habits.reduce((sum, h) => sum + h.streak, 0);
        const average = Math.round(totalStreak / habits.length);
        setHabitAdherence({
          percentage: Math.min(100, Math.round((average / 30) * 100)), // Assuming 30-day target
          average,
        });
      } else {
        setHabitAdherence({ percentage: 0, average: 0 });
      }
    } catch (error) {
      console.error('Failed to load metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    // Simple CSV export
    const csv = `Metric,Value\nRitual Completion,${ritualCompletion.percentage}%\nHabit Adherence,${habitAdherence.percentage}%\nCurrent Streak,${streak} days`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sovereign-mind-review-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="space-y-8 w-full">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <Skeleton variant="text" className="h-10 w-64" />
          <Skeleton variant="rectangular" className="h-10 w-32" />
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 w-full">
      <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold mb-2">Weekly Review</h1>
          <p className="text-lg text-muted-foreground">Track your progress and insights</p>
        </div>
        <Button variant="outline" onClick={handleExport} className="shrink-0">
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </header>

      <div className="grid gap-6 md:grid-cols-3">
        <Card className="p-8 hover:shadow-lg transition-shadow">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-lg bg-primary/10">
              <BarChart3 className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-xl font-semibold">Ritual Completion</h3>
          </div>
          <p className="text-5xl font-bold mb-3 text-primary">{ritualCompletion.percentage}%</p>
          <p className="text-sm text-muted-foreground">
            {ritualCompletion.completed} of {ritualCompletion.total} days this week
          </p>
        </Card>

        <Card className="p-8 hover:shadow-lg transition-shadow">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-lg bg-accent/10">
              <BarChart3 className="h-6 w-6 text-accent" />
            </div>
            <h3 className="text-xl font-semibold">Habit Adherence</h3>
          </div>
          <p className="text-5xl font-bold mb-3 text-accent">{habitAdherence.percentage}%</p>
          <p className="text-sm text-muted-foreground">
            Average streak: {habitAdherence.average} days
          </p>
        </Card>

        <Card className="p-8 hover:shadow-lg transition-shadow">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-lg bg-repair-warm/10">
              <BarChart3 className="h-6 w-6 text-repair-warm" />
            </div>
            <h3 className="text-xl font-semibold">Current Streak</h3>
          </div>
          <p className="text-5xl font-bold mb-3 text-repair-warm">{streak}</p>
          <p className="text-sm text-muted-foreground">Days in a row</p>
        </Card>
      </div>
    </div>
  );
}
